import { compatWrapper } from "@backstage/core-compat-api";
import {
	Sidebar,
	sidebarConfig,
	SidebarDivider,
	SidebarGroup,
	SidebarItem,
	SidebarScrollWrapper,
	SidebarSpace,
	useSidebarOpenState,
	Link,
} from "@backstage/core-components";
import { NotificationsSidebarItem } from "@backstage/plugin-notifications";
import { SidebarSearchModal } from "@backstage/plugin-search";
import {
	Settings as SidebarSettings,
	UserSettingsSignInAvatar,
} from "@backstage/plugin-user-settings";
import { makeStyles } from "@material-ui/core";
import FeedbackOutlinedIcon from "@material-ui/icons/FeedbackOutlined";
import HomeOutlinedIcon from "@material-ui/icons/HomeOutlined";
import MenuOutlinedIcon from "@material-ui/icons/MenuOutlined";
import SearchIcon from "@material-ui/icons/Search";

import { AdminSidebarItem, AvdSidebarItem } from "../avd/avdEntitlement";
import { WebAppsSidebarItem } from "../swa/swaEntitlement";

import { DeployedCommit } from "./DeployedCommit";
import LogoFull from "./LogoFull";
import LogoIcon from "./LogoIcon";

// The logo block. It needs real height and the full open width because LogoFull is a two-line
// lockup — a closed-drawer width silently clips it.
//
// The hairline separates brand from nav items, and uses `--bui-border-1` so it stays one color with
// the sidebar's dividers in theme.ts.
const useSidebarLogoStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		height: 2.5 * sidebarConfig.logoHeight,
		display: "flex",
		flexFlow: "row nowrap",
		alignItems: "center",
		borderBottom: "1px solid var(--bui-border-1)",
		marginBottom: theme.spacing(1),
	},
	link: {
		marginLeft: 24,
	},
}));

const SidebarLogo = () => {
	const classes = useSidebarLogoStyles();
	const { isOpen } = useSidebarOpenState();

	return (
		<div className={classes.root}>
			<Link to="/" underline="none" className={classes.link} aria-label="Home">
				{isOpen ? <LogoFull /> : <LogoIcon />}
			</Link>
		</div>
	);
};

// THE APP'S NAV. It has no <SidebarPage> wrapper and no `children` prop because the default
// `app/layout` extension supplies both and slots this component into its `nav` input.
//
// The items are hard-coded deliberately. Auto-discovery via `props.navItems` only sees page
// extensions that declare `title` + `icon`, which the legacy-collected routes don't — so the sidebar
// would lose most of itself. navigation.test.ts asserts every item by name, so switching to
// `navItems.take(id)` / `rest()` is checkable.
//
// Catalog, My Groups, APIs, Docs and Create... are deliberately absent from the sidebar for the
// broader-IM launch. They stay registered in App.tsx because the scaffolder's task page depends on
// their APIs and route refs; only the sidebar entry points are absent, the URLs still work.
//
// compatWrapper is required: Sidebar and friends are legacy components, and AvdSidebarItem calls
// useApi(identityApiRef).
export const AppNav = () =>
	compatWrapper(
		<Sidebar>
			<SidebarLogo />
			<SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
				<SidebarSearchModal />
			</SidebarGroup>
			<SidebarDivider />
			<SidebarGroup label="Menu" icon={<MenuOutlinedIcon />}>
				{/* Global nav, not org-specific */}
				<SidebarItem icon={HomeOutlinedIcon} to="/" text="Home" />
				<AvdSidebarItem />
				<WebAppsSidebarItem />
				{/* Directly under My AVD, and hidden from everyone who is not on the platform team.
				Both AVD items are entitlement-gated, but on DIFFERENT groups — see useAvdAdmin. */}
				<AdminSidebarItem />
				{/* End global nav */}
				<SidebarDivider />
				<SidebarScrollWrapper>
					{/* Items in this group will be scrollable if they run out of space */}
				</SidebarScrollWrapper>
			</SidebarGroup>
			<SidebarSpace />
			<SidebarDivider />
			{/* THE UTILITY BLOCK — pushed to the bottom by <SidebarSpace>, which eats the remaining
			height. Notifications and Feedback sit here TOGETHER, between the same pair of dividers
			and with no divider between them, mirroring IM GPT's bottom-left cluster: neither is a
			destination you navigate to as part of a task, so neither belongs in the "Menu" group
			above with Home and My AVD.

			Feedback deep-links into the scaffolder's template detail route, which is why "Create..."
			being absent from this sidebar does not affect it — the route is
			`sub-page:scaffolder/templates`, deliberately kept enabled in app-config.yaml's
			`app.extensions`. Unlike AvdSidebarItem it has no entitlement hook: the `feedback`
			template carries no `avd` tag, so the permission policy leaves it visible to every
			signed-in colleague, and hiding the only feedback channel from most of the audience
			would defeat the point. */}
			<NotificationsSidebarItem />
			<SidebarItem
				icon={FeedbackOutlinedIcon}
				to="/create/templates/default/feedback"
				text="Feedback"
			/>
			<SidebarDivider />
			<DeployedCommit />
			<SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
				<SidebarSettings />
			</SidebarGroup>
		</Sidebar>,
	);
